// Avisos de lectura fallida del wizard. Una lectura caída y un dato de negocio que no existe llegan
// iguales —lista vacía, monto ausente—, así que cada pantalla tiene que distinguirlos en voz alta.
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/kit';
import { ApiError } from '@/api/errors';
import { cn } from '@/lib/cn';

export interface QueryErrorNoticeProps {
    /** Qué no se pudo hacer, en una frase. */
    title: string;
    /** El error de la query. `null`/`undefined` no pinta nada. */
    error: unknown;
    /** Detalle cuando el error no trae envelope Fiado (red caída, error del navegador). */
    fallback: string;
    /** Qué tiene que hacer el vendedor. Se omite cuando el título ya lo dice. */
    hint?: string;
    onRetry?: () => void;
    retrying?: boolean;
    className?: string;
}

/**
 * El bloque que ocupa una sección entera: lo que falló, el detalle del silo y con qué reintentar.
 * El `code` va a la vista porque es lo que el vendedor le dicta a soporte.
 */
export function QueryErrorNotice({
    title,
    error,
    fallback,
    hint,
    onRetry,
    retrying = false,
    className,
}: QueryErrorNoticeProps) {
    if (!error) return null;

    const isApi = error instanceof ApiError;
    const detail = isApi ? (error.description ?? error.msg) : fallback;

    return (
        <div
            role="status"
            className={cn(
                'mx-auto mb-5 flex max-w-md items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-left',
                className,
            )}
        >
            <AlertTriangle className="mt-0.5 size-4 flex-none text-destructive" aria-hidden />
            <div className="text-[12px] leading-relaxed text-destructive">
                <p className="font-medium">{title}</p>
                <p>{detail}</p>
                {isApi && (
                    <p className="mt-1 font-mono text-[10.5px] opacity-80">
                        {error.httpStatus} · {error.code}
                    </p>
                )}
                {hint && <p className="mt-1 text-muted-foreground">{hint}</p>}
                {onRetry && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        isLoading={retrying}
                        onClick={onRetry}
                    >
                        Reintentar
                    </Button>
                )}
            </div>
        </div>
    );
}

export interface InlineQueryErrorProps {
    /** Ya resuelto por quien lo pinta, normalmente con `wizardErrorMsg`. */
    message: string;
    onRetry?: () => void;
    retrying?: boolean;
    /** Tamaño y márgenes los pone la pantalla: esto vive debajo de un campo o dentro de una franja. */
    className?: string;
}

/** La línea suelta, para donde un bloque no cabe: debajo de un campo o en lugar de un dato. */
export function InlineQueryError({
    message,
    onRetry,
    retrying = false,
    className,
}: InlineQueryErrorProps) {
    return (
        <p
            role="status"
            className={cn('flex flex-wrap items-center gap-2 text-destructive', className)}
        >
            {message}
            {onRetry && (
                <Button variant="outline" size="sm" isLoading={retrying} onClick={onRetry}>
                    Reintentar
                </Button>
            )}
        </p>
    );
}
